import React from 'react';

type PointsAccrualItem = {
  id: string;
  level: string;
  stageCode?: string | null;
  stageName?: string | null;
  isWinner: boolean;
  winPoints: number;
  stagePoints: number;
  awardedPoints: number;
  player: {
    id: string;
    name: string;
  };
};

type PointsAccrualDocument = {
  id: string;
  tournamentId: string;
  status: 'DRAFT' | 'POSTED' | string;
  accrualDate?: string | null;
  itemsCount: number;
  totalPoints: number;
  notes?: string | null;
  createdAt: string;
  tournament?: {
    id: string;
    name: string;
    status: string;
  };
  items: PointsAccrualItem[];
};

type Props = {
  open: boolean;
  onClose: () => void;
  document: PointsAccrualDocument | null;
  onRecalculate?: () => void | Promise<void>;
  onSaveRecalculation?: () => void | Promise<void>;
  showSaveButton?: boolean;
  recalculateLoading?: boolean;
  saveLoading?: boolean;
  onChangeAccrualDate?: (nextIsoDate: string) => void | Promise<void>;
  accrualDateSaving?: boolean;
};

const TournamentPointsAccrualModal: React.FC<Props> = ({
  open,
  onClose,
  document,
  onRecalculate,
  onSaveRecalculation,
  showSaveButton = false,
  recalculateLoading = false,
  saveLoading = false,
  onChangeAccrualDate,
  accrualDateSaving = false
}) => {
  if (!open || !document) return null;

  const statusLabel = document.status === 'DRAFT'
    ? 'Черновик'
    : document.status === 'POSTED'
      ? 'Проведен'
      : document.status;

  const getLevelSortKey = (level: string): [string, number, number, string] => {
    const [familyRaw, rankRaw] = String(level || '').split('-');
    const family = familyRaw || 'Z';
    let rank = Number.MAX_SAFE_INTEGER;
    let sub = 0;

    if (rankRaw) {
      if (rankRaw === 'ProAm') {
        rank = 100;
      } else if (rankRaw === 'Mixt') {
        rank = 200;
      } else {
        const numericTokenMatch = /^(\d+(?:\.\d+)?)/.exec(rankRaw);
        const numeric = numericTokenMatch ? Number(numericTokenMatch[1]) : Number.NaN;
        if (Number.isFinite(numeric)) {
          rank = numeric;
          if (String(numericTokenMatch?.[1] || '').includes('.')) sub = 1;
        }
      }
    }

    return [family, rank, sub, level];
  };

  const getStageOrder = (stageCode?: string | null): number => {
    const code = String(stageCode || '').toUpperCase();
    const placeMatch = /^GROUP_PLACE_(\d+)$/.exec(code);
    if (placeMatch) {
      const place = Number(placeMatch[1]);
      if (Number.isFinite(place) && place > 0) {
        return 80 + place;
      }
    }

    const order: Record<string, number> = {
      GROUP_STAGE: 10,
      GROUP_1: 11,
      GROUP_2: 12,
      GROUP_3: 13,
      ROUND_OF_64: 20,
      ROUND_OF_32: 30,
      ROUND_OF_16: 40,
      QUARTERFINAL: 50,
      SEMIFINAL: 60,
      THIRD_PLACE: 65,
      FINAL: 70,
      PLAYOFF: 25,
      CUSTOM_ROUND: 35
    };
    return order[code] ?? 999;
  };

  const sortedItems = React.useMemo(() => {
    const arr = [...(document.items || [])];
    arr.sort((a, b) => {
      const [af, ar, as, araw] = getLevelSortKey(a.level);
      const [bf, br, bs, braw] = getLevelSortKey(b.level);
      if (af !== bf) return af.localeCompare(bf);
      if (ar !== br) return ar - br;
      if (as !== bs) return as - bs;
      if (araw !== braw) return araw.localeCompare(braw);

      const stageA = getStageOrder(a.stageCode);
      const stageB = getStageOrder(b.stageCode);
      if (a.awardedPoints !== b.awardedPoints) return b.awardedPoints - a.awardedPoints;
      if (a.isWinner !== b.isWinner) return a.isWinner ? -1 : 1;
      if (stageA !== stageB) return stageA - stageB;

      return String(a.player?.name || '').localeCompare(String(b.player?.name || ''), 'ru');
    });
    return arr;
  }, [document.items]);

  const accrualDateValue = React.useMemo(() => {
    const source = document.accrualDate || document.createdAt;
    const d = new Date(source);
    if (Number.isNaN(d.getTime())) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }, [document.accrualDate, document.createdAt]);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-5xl max-h-[90vh] overflow-y-auto">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Документ начисления очков
            </h3>
            <p className="text-xs text-gray-500">
              Турнир: {document.tournament?.name || document.tournamentId}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {showSaveButton && onSaveRecalculation && (
              <button
                type="button"
                onClick={onSaveRecalculation}
                disabled={saveLoading || recalculateLoading}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-60"
              >
                {saveLoading ? 'Сохранение...' : 'Сохранить'}
              </button>
            )}
            {onRecalculate && (
              <button
                type="button"
                onClick={onRecalculate}
                disabled={recalculateLoading || saveLoading}
                className="px-3 py-1.5 text-sm bg-amber-100 text-amber-800 rounded hover:bg-amber-200 disabled:opacity-60"
              >
                {recalculateLoading ? 'Пересчет...' : 'Пересчет'}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
            >
              Закрыть
            </button>
          </div>
        </div>

        <div className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 text-sm">
            <div className="bg-gray-50 rounded p-2">
              <div className="text-gray-500 text-xs">Статус</div>
              <div className="font-medium">{statusLabel}</div>
            </div>
            <div className="bg-gray-50 rounded p-2">
              <div className="text-gray-500 text-xs">Строк</div>
              <div className="font-medium">{document.itemsCount}</div>
            </div>
            <div className="bg-gray-50 rounded p-2">
              <div className="text-gray-500 text-xs">Сумма очков</div>
              <div className="font-medium">{document.totalPoints}</div>
            </div>
            <div className="bg-gray-50 rounded p-2">
              <div className="text-gray-500 text-xs">Дата создания</div>
              <div className="font-medium">
                {new Date(document.createdAt).toLocaleString('ru-RU')}
              </div>
            </div>
            <div className="bg-gray-50 rounded p-2">
              <div className="text-gray-500 text-xs mb-1">Дата начисления</div>
              {document.status === 'DRAFT' && onChangeAccrualDate ? (
                <input
                  type="date"
                  value={accrualDateValue}
                  onChange={(e) => onChangeAccrualDate(e.target.value)}
                  disabled={accrualDateSaving}
                  className="border border-gray-300 rounded px-2 py-1 text-sm"
                />
              ) : (
                <div className="font-medium">
                  {new Date(document.accrualDate || document.createdAt).toLocaleDateString('ru-RU')}
                </div>
              )}
            </div>
          </div>

          {document.notes && (
            <div className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
              {document.notes}
            </div>
          )}

          <div className="overflow-auto border border-gray-200 rounded max-h-[60vh]">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2 text-left font-medium text-gray-600">Игрок</th>
                  <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2 text-left font-medium text-gray-600">Уровень</th>
                  <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2 text-left font-medium text-gray-600">Этап</th>
                  <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2 text-left font-medium text-gray-600">Результат</th>
                  <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2 text-right font-medium text-gray-600">Очки за победу</th>
                  <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2 text-right font-medium text-gray-600">Очки за этап</th>
                  <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2 text-right font-medium text-gray-600">Начислено</th>
                </tr>
              </thead>
              <tbody>
                {sortedItems.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-3 py-8 text-center text-gray-500">
                      Для этого турнира не найдено строк начисления.
                    </td>
                  </tr>
                ) : (
                  sortedItems.map((item) => (
                    <tr key={item.id} className="border-t border-gray-100">
                      <td className="px-3 py-2">{item.player?.name || item.player?.id}</td>
                      <td className="px-3 py-2">{item.level}</td>
                      <td className="px-3 py-2">{item.stageName || item.stageCode || '-'}</td>
                      <td className="px-3 py-2">{item.isWinner ? 'Победа' : 'Поражение'}</td>
                      <td className="px-3 py-2 text-right">{item.winPoints}</td>
                      <td className="px-3 py-2 text-right">{item.stagePoints}</td>
                      <td className="px-3 py-2 text-right font-semibold">{item.awardedPoints}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TournamentPointsAccrualModal;
